import React, { useState, useCallback } from 'react';
import { APIProvider, Map as GoogleMap, AdvancedMarker } from '@vis.gl/react-google-maps';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { MapPin, Loader2, ExternalLink } from 'lucide-react';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { waitForGeocoder, reverseGeocode, GOOGLE_MAPS_API_KEY } from '../../utils/geocode';
import { NOMINATIM_BASE } from '../../utils/nominatim';

const GOOGLE_MAPS_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';

/**
 * Inline location popover: click the coordinate to peek a small map + reverse-geocoded
 * street address without leaving the page. Reuses the app's geocode strategy (Google
 * Geocoder when the `google_reverse_geocoding` feature is on + Maps loaded, else Nominatim).
 */
export const LocationPopover = ({ loc, testid }) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState(null);
  const [addrLoading, setAddrLoading] = useState(false);

  const hasLoc = loc && loc.latitude != null && loc.longitude != null;
  const lat = hasLoc ? Number(loc.latitude) : 0;
  const lng = hasLoc ? Number(loc.longitude) : 0;

  const fetchAddress = useCallback(async () => {
    setAddrLoading(true);
    setAddress(null);
    // When the flag is ON and a Google key exists, Google IS the intended provider — wait for
    // its Geocoder to load (the inline map triggers the script load) instead of giving up early.
    const wantGoogle = isFeatureEnabled('google_reverse_geocoding') && !!GOOGLE_MAPS_API_KEY;
    if (wantGoogle) await waitForGeocoder();
    const addr = await reverseGeocode(lat, lng, { useGoogle: wantGoogle, nominatimBaseUrl: NOMINATIM_BASE });
    setAddress(addr);
    setAddrLoading(false);
  }, [lat, lng, isFeatureEnabled]);

  const onOpenChange = (o) => {
    setOpen(o);
    if (o && address === null && !addrLoading) fetchAddress();
  };

  if (!hasLoc) return <span className="text-muted-foreground">-</span>;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[#2563eb] hover:underline"
          onClick={(e) => e.stopPropagation()}
          title="View last detected location"
          data-testid={testid}
        >
          <MapPin className="w-3 h-3 shrink-0" /> {lat.toFixed(5)}, {lng.toFixed(5)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[300px] p-0 z-[10000] overflow-hidden"
        align="start"
        onClick={(e) => e.stopPropagation()}
        data-testid={testid ? `${testid}-popover` : undefined}
      >
        <div className="h-[160px] w-full bg-muted">
          {GOOGLE_MAPS_API_KEY ? (
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
              <GoogleMap
                defaultCenter={{ lat, lng }}
                defaultZoom={15}
                mapId={GOOGLE_MAPS_MAP_ID}
                gestureHandling="greedy"
                disableDefaultUI
                style={{ width: '100%', height: '100%' }}
              >
                <AdvancedMarker position={{ lat, lng }}>
                  <MapPin className="w-7 h-7 text-red-600 drop-shadow" fill="#ef4444" />
                </AdvancedMarker>
              </GoogleMap>
            </APIProvider>
          ) : (
            <div className="flex h-full items-center justify-center text-xs text-muted-foreground">Map unavailable</div>
          )}
        </div>
        <div className="space-y-1 p-3 text-xs">
          <div className="flex items-start gap-1.5">
            <MapPin className="mt-0.5 w-3.5 h-3.5 shrink-0 text-[#2563eb]" />
            <span className="font-medium" data-testid={testid ? `${testid}-address` : undefined}>
              {addrLoading
                ? <span className="flex items-center gap-1 text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Looking up address…</span>
                : (address || '—')}
            </span>
          </div>
          {loc.source_label && <p className="text-muted-foreground">{loc.source_label}</p>}
          <p className="font-mono text-muted-foreground">{lat.toFixed(6)}, {lng.toFixed(6)}</p>
          <a
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[#2563eb] hover:underline pt-1"
          >
            <ExternalLink className="w-3 h-3" /> Open in Google Maps
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
};
